import React, { useRef, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChart = ({ datas, labels, borderColor, chartdata }) => {
  // Format numbers to K format (thousands)
  const formatToK = (value) => {
    if (value === 0) return '₹0';
    const num = Math.abs(value);
    if (num >= 1000) {
      const rounded = Math.round(num / 1000);
      const sign = value < 0 ? '-' : '';
      return `${sign}₹${rounded}K`;
    }
    // For values less than 1000, show the actual number
    const sign = value < 0 ? '-' : '';
    return `${sign}₹${Math.round(num)}`;
  };

  // Format dates to be shorter and more readable
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      return dateString;
    }
  };

  // Use provided datas and labels if available, otherwise fall back to chartdata
  const chartlabel = labels && labels.length > 0
    ? labels.map(label => {
        // Check if it's a date string (YYYY-MM-DD format)
        if (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}/.test(label)) {
          return formatDate(label);
        }
        return label;
      })
    : (chartdata && chartdata.length > 0
      ? chartdata.map((item) => formatDate(item.run_date))
      : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"]);
  
  const chartvalues = datas && datas.length > 0
    ? datas
    : (chartdata && chartdata.length > 0
      ? chartdata.map((item) => item.cumulative_pnl)
      : [-10, 10, 120, 20, 10, 100]);

  const labelss = chartlabel || ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  const values = chartvalues || [-10, 10, 120, 20, 10, 100];
  
  // Use logo dark blue color for cumulative line chart
  const lineColor = borderColor || '#053A62';
  const fillColor = lineColor + '20'; // Semi-transparent fill

  const data = {
    labels: labelss,
    datasets: [
      {
        label: "Cumulative Profit",
        data: values,
        fill: true,
        backgroundColor: fillColor,
        borderColor: lineColor,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: lineColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        pointHoverBackgroundColor: lineColor,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: 'rgba(22, 26, 35, 0.9)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: '600'
        },
        bodyFont: {
          size: 12
        },
        borderColor: lineColor,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            const sign = value >= 0 ? '+' : '';
            return `Cumulative: ${sign}₹${value.toLocaleString('en-IN')}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11,
            family: "'Outfit', sans-serif"
          },
          color: '#6c757d',
          padding: 8,
          callback: function(value, index) {
            const label = this.getLabelForValue(value);
            // If label is too long, show every nth label
            if (labelss.length > 10) {
              return index % Math.ceil(labelss.length / 8) === 0 ? label : '';
            }
            return label;
          }
        },
        title: {
          display: false,
        },
      },
      y: {
        grid: {
          display: true,
          drawBorder: false,
          color: 'rgba(234, 234, 234, 0.5)',
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 11,
            family: "'Outfit', sans-serif"
          },
          color: '#6c757d',
          padding: 8,
          callback: function(value) {
            return formatToK(value);
          }
        },
        border: {
          display: false
        },
        title: {
          display: false,
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default LineChart;
